import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Box, Tooltip } from '@mui/material';
import {
  insuredLenderNameSortingFn,
  momentDateSortingFn,
  priorityNumberSortingFn
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/sorting';
import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import { MasterPoolTableFields } from '../../HbtMasterPool/types';
import styles from '../styles.module.scss';
import {
  createInsuredLenderNameRenderer,
  createTermInsuranceRenderer,
  renderRequestReceiptDateCell,
  renderInsuredLenderCodeCell,
  renderMasterPoolCell,
  createStatusRenderer,
  renderTotalLoansSubmitted,
  createRenderTotalAmountSubmitted,
  createRenderRateWithPercentage,
  renderString,
  createOccupancyTypeRenderer,
  createPoolTypeRenderer,
  createRequestTypeRenderer,
  handleHistoricalRemarks,
  renderFraudDetectionDateCell
} from './masterPoolInventoryTableCellRenderers';
import { renderDataTestId } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/vriTableColumns';

export const createLoansInventoryColumns = (
  t: TFunction,
  language: string,
  setSelectedMasterPoolForRemark: React.Dispatch<React.SetStateAction<(number | null)[]>>,
  toggleHistoricalRemarksDrawer: (newOpen: boolean) => void
): MRT_ColumnDef<MasterPoolTableFields>[] => {
  const insuredLenderNameRenderer = createInsuredLenderNameRenderer(language);
  const termInsuranceRenderer = createTermInsuranceRenderer(t);
  const renderTotalAmountSubmitted = createRenderTotalAmountSubmitted(language);
  const renderRateWithPercentage = createRenderRateWithPercentage(language, 4);
  const renderRateWithPercentageForPremiumDiscount = createRenderRateWithPercentage(language, 1);
  const renderStatus = createStatusRenderer(t);
  const renderMasterPoolCellRender = renderMasterPoolCell(t);
  const ocupancyType = createOccupancyTypeRenderer(t);
  const renderPoolType = createPoolTypeRenderer(t);
  const requestType = createRequestTypeRenderer(t);
  const handleHistoricalRemarksIcon = handleHistoricalRemarks(
    t,
    setSelectedMasterPoolForRemark,
    toggleHistoricalRemarksDrawer
  );

  return [
    {
      accessorKey: 'latestRemark',
      header: 'latestRemark',
      Header: ({ column }) => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_comment" />,
      Cell: handleHistoricalRemarksIcon,
      size: 72,
      enableSorting: false,
      enableResizing: false,
      muiTableHeadCellProps: renderDataTestId('latestRemark')
    },
    {
      accessorKey: 'requestReceiptDate' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-RequestReceiptDate'),
      Cell: renderRequestReceiptDateCell,
      enableSorting: true,
      size: language === 'en' ? 250 : 350,
      sortingFn: momentDateSortingFn,
      muiTableHeadCellProps: renderDataTestId('requestReceiptDate')
    },
    {
      accessorKey: 'masterPoolCmhcAccountNumber' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-MasterPoolNumber'),
      Cell: renderMasterPoolCellRender,
      enableSorting: true,
      size: language === 'en' ? 250 : 270,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('masterPoolCmhcAccountNumber')
    },
    {
      accessorKey: 'masterPoolAccountStatusCode' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-Status'),
      Cell: renderStatus,
      enableSorting: true,
      size: 140,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('masterPoolAccountStatusCode')
    },
    {
      accessorKey: 'insuredApprovedLenderCode' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-InsuredApprovedLenderCode'),
      Cell: renderInsuredLenderCodeCell,
      enableSorting: true,
      size: 320,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('insuredApprovedLenderCode')
    },
    {
      accessorKey: 'insuredApprovedLenderName' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-InsuredApprovedLenderName'),
      Cell: insuredLenderNameRenderer,
      enableSorting: true,
      size: 320,
      sortingFn: insuredLenderNameSortingFn,
      muiTableHeadCellProps: renderDataTestId('insuredApprovedLenderName')
    },
    {
      accessorKey: 'occupancyTypeCode' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-OccupancyType'),
      Cell: ocupancyType,
      enableSorting: true,
      size: language === 'en' ? 230 : 290,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('occupancyTypeCode')
    },
    {
      accessorKey: 'termInsuranceYearCount' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-TermInsurance'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Pi-Master-Pool-Term-Insurance-Tooltip')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      Cell: termInsuranceRenderer,
      enableSorting: true,
      size: language === 'en' ? 250 : 270,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('termInsuranceYearCount')
    },
    {
      accessorKey: 'numberOfLoansSubmitted' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-TotalLoansSubmitted'),
      Cell: renderTotalLoansSubmitted,
      enableSorting: true,
      size: 250,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('numberOfLoansSubmitted')
    },
    {
      accessorKey: 'totalAmountSubmitted' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-TotalAmountSubmitted'),
      Cell: renderTotalAmountSubmitted,
      enableSorting: true,
      size: 280,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('totalAmountSubmitted')
    },
    {
      accessorKey: 'fraudDetectionDate' as const,
      header: t('Fraud-Detection-Date'),
      Cell: renderFraudDetectionDateCell,
      enableSorting: true,
      size: language === 'en' ? 250 : 350,
      sortingFn: momentDateSortingFn,
      muiTableHeadCellProps: renderDataTestId('fraudDetectionDate')
    },
    {
      accessorKey: 'totalLoansInsured' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-TotalInsuredLoans'),
      Cell: renderTotalLoansSubmitted,
      enableSorting: true,
      size: 250,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('totalLoansInsured')
    },
    {
      accessorKey: 'totalInsuredAmount' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-TotalInsuredAmount'),
      Cell: renderTotalAmountSubmitted,
      enableSorting: true,
      size: 250,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('totalInsuredAmount')
    },
    {
      accessorKey: 'approvalDate' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-ApprovalDate'),
      Cell: renderRequestReceiptDateCell,
      enableSorting: true,
      size: 250,
      sortingFn: momentDateSortingFn,
      muiTableHeadCellProps: renderDataTestId('approvalDate')
    },
    {
      accessorKey: 'basePremiumRate' as const,
      header: t('Base-Premium-Rate'),
      Cell: renderRateWithPercentage,
      enableSorting: true,
      size: 240,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('basePremiumRate')
    },
    {
      accessorKey: 'premiumDiscount' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-PremiumDiscount'),
      Cell: renderRateWithPercentageForPremiumDiscount,
      enableSorting: true,
      size: 240,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('premiumDiscount')
    },
    {
      accessorKey: 'premiumRate' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-PremiumRate'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Pi-Master-Pool-Premium-Rate')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      Cell: renderRateWithPercentage,
      enableSorting: true,
      size: 240,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('premiumRate')
    },
    {
      accessorKey: 'reasonForWithdrawal' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-ReasonForWithdrawal'),
      Cell: renderString,
      enableSorting: true,
      size: 250,
      muiTableHeadCellProps: renderDataTestId('reasonForWithdrawal')
    },
    {
      accessorKey: 'requestType' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Column-RequestType'),
      Cell: requestType,
      enableSorting: true,
      size: language === 'en' ? 190 : 200,
      muiTableHeadCellProps: renderDataTestId('requestType')
    },
    {
      accessorKey: 'poolBundleTypeCode' as const,
      header: t('Pi-Master-Pool-Inventory-Table-Pool-Bundle-Type'),
      Cell: renderPoolType,
      enableSorting: true,
      size: 250,
      muiTableHeadCellProps: renderDataTestId('poolBundleTypeCode')
    }
  ];
};
